import { BaseApiClient, QueryParams } from "./base-api"
import { 
  DeleteResponse,
  GenerationRequest,
  GenerationResponse
} from "@/schemas/api-responses"

const API_BASE_URL = import.meta.env.VITE_BASE_URL

// Video-specific interfaces
export interface UserVideo {
  updated_at: any
  id: string
  video_url: string
  thumbnail_url: string
  title: string
  file_size: number
  created_at: string
  user_email?: string
  user_id?: string
  path?: string
  status?: string
  feedback?: string
  prompt?: string
  model?: string
  video_type?: string
  image_type?: string // Optional to support image objects
  is_pinned?: boolean
  pinned_by?: string
  pinned_at?: string
  duration?: number
  delete_request?: boolean
  aspect_ratio?: string
  is_favourited?: boolean
  is_archived?: boolean
  archived_by?: string
  archived_at?: string
  extended?: boolean
  original_video_id?: string  // For extended videos created from last frame
}

export interface PinVideoResponse {
  message: string
  video_id: string
  is_pinned: boolean
  pinned_by?: string
  pinned_at?: string
}

export interface FavouriteResponse {
  success: boolean
  is_favourited: boolean
  message: string
  video_id: string
  error?: string
}

export interface ArchiveResponse {
  success: boolean
  is_archived: boolean
  message: string
  video_id: string
  error?: string
}

// Extend shared generation interfaces for video-specific functionality  
export interface VideoGenerationRequest extends GenerationRequest {
  // Video-specific generation parameters can be added here
  idempotency_key?: string
}

export type VideoGenerationResponse = GenerationResponse

export interface VideoUploadResponse {
  success: boolean
  message: string
  job_id?: string
  video_info?: {
    id: string
    url: string
    title: string
    thumbnail: string
    created_at: string
    size: number
    status: string
    feedback: string
    prompt: string
    model: string
    user_email: string
    user_id: string
    video_type: string
  }
  upload_path?: string
}

export interface VideoStatusResponse {
  status: string
  video_url?: string
  thumbnail_url?: string
  progress?: number
  message?: string
}

export interface VideoStatusCountsResponse {
  approved: number
  pending: number
  rejected: number
  total: number
  approved_duration_minutes: number
  pending_duration_minutes: number
  rejected_duration_minutes: number
  total_duration_minutes: number
}

// These need to maintain original structure for compatibility with existing components
export interface VideoListResponse {
  success: boolean
  videos: UserVideo[]
  total_count: number
  total_unfiltered_count?: number
  limit: number
  offset: number
  has_more: boolean
}

export interface VideoResponse {
  success: boolean
  video: UserVideo
}

// These can use shared types as they match the structure
export type DeleteVideoResponse = DeleteResponse

export interface VideoListParams {
  limit?: number
  offset?: number
  status?: string
  user_email?: string
  sort_by?: string
  video_type?: string
}

export interface User {
  email: string
  count: number
  role: string
}

export interface UsersResponse {
  users: User[]
}

export interface PinnedVideosResponse {
  success: boolean
  videos: UserVideo[]
}

export interface VideoExtensionRequest {
  video_id: string
  prompt: string
  target_duration?: number
}

export interface VideoExtensionResponse {
  success: boolean
  message: string
  task_id?: string
  extended_video_id?: string
  estimated_duration?: number
  extension_request_id?: string  // Set for regular users (pending admin approval)
}

export interface VideoExtensionRequestRecord {
  id: string
  video_id: string
  user_id: string
  user_email: string
  prompt: string
  target_duration: number
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed' | 'failed'
  feedback?: string
  reviewed_by?: string
  reviewed_at?: string
  task_id?: string
  extended_video_id?: string
  created_at: string
  // Embedded video preview fields (returned by paginated endpoints)
  video_url?: string | null
  thumbnail_url?: string | null
  aspect_ratio?: string | null
  duration?: number | null
}

export interface PaginatedExtensionRequestsResponse {
  items: VideoExtensionRequestRecord[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

// Remove duplicate helper functions - using BaseApiClient methods instead

/**
 * Fetch a shared video by ID — accessible to any authenticated approved user
 * @param videoId - The video ID to fetch
 * @returns Promise<UserVideo | null>
 */
export const fetchSharedVideo = async (videoId: string): Promise<UserVideo | null> => {
  try {
    const response = await BaseApiClient.makeAuthenticatedRequest(
      `${API_BASE_URL}/picasso/video/shared/${videoId}`,
      { method: "GET" }
    )
    if (!response.ok) {
      return null
    }
    const data = await response.json()
    return data.video || null
  } catch (err) {
    console.error("Error fetching shared video:", err)
    return null
  }
}

/**
 * Fetch a specific video by ID (works for both admin and user contexts)
 * @param videoId - The video ID to fetch
 * @param isAdmin - Whether to use admin endpoint (default: false)
 * @returns Promise<UserVideo | null>
 */
export const fetchVideoById = async (
  videoId: string,
  isAdmin: boolean = false
): Promise<UserVideo | null> => {
  try {    
    const endpoint = isAdmin 
      ? `/picasso/video/admin/video/${videoId}`
      : `/picasso/video/video/${videoId}`

    const response = await BaseApiClient.makeAuthenticatedRequest(`${API_BASE_URL}${endpoint}`, {
      method: "GET",
    })

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    return data.video || null
  } catch (err) {
    console.error("Error fetching video by ID:", err);
    return null
  }
}

/**
 * Fetch pinned videos (works for both admin and user contexts)
 * @param isAdmin - Whether to use admin endpoint (default: false)
 * @returns Promise<UserVideo[]>
 */
export const fetchPinnedVideos = async (isAdmin: boolean = false): Promise<UserVideo[]> => {
  try {
    const endpoint = isAdmin
      ? "/picasso/video/pinned_videos"
      : "/picasso/video/user/pinned_videos"

    const response = await BaseApiClient.makeAuthenticatedRequest(`${API_BASE_URL}${endpoint}`, {
      method: "GET",
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const data = await response.json()
    return data.videos || []
  } catch (err) {
    throw new Error(BaseApiClient.handleApiError(err, "fetch pinned videos"))
  }
}

/**
 * Common URL builder for video list endpoints
 * @param params - Query parameters
 * @param isAdmin - Whether to use admin endpoint (default: false)
 * @returns string - Built URL
 */
export const buildVideoListUrl = (
  params: {
    limit?: number
    offset?: number
    status?: string
    user_email?: string
    sort_by?: string
  } = {},
  isAdmin: boolean = false
): string => {
  const baseEndpoint = isAdmin 
    ? "/picasso/video/all"
    : "/picasso/video/list"
  
  return BaseApiClient.buildUrlWithParams(`${API_BASE_URL}${baseEndpoint}`, params)
}

/**
 * Common error status handler
 * @param status - HTTP status code
 * @param operation - Operation description
 * @returns string - Error message
 */
export const getErrorMessageByStatus = (status: number, operation: string): string => {
  switch (status) {
    case 401:
      return "Unauthorized access"
    case 403:
      return "Insufficient permissions"
    case 404:
      return `${operation} not found`
    case 500:
      return "Internal server error"
    default:
      return `Failed to ${operation}: HTTP ${status}`
  }
}

// Remove duplicate helper functions - using BaseApiClient methods instead

/**
 * Pin a video (works for both admin and user contexts)
 * @param videoId - The video ID to pin
 * @returns Promise<PinVideoResponse>
 */
export const pinVideo = async (videoId: string): Promise<PinVideoResponse> => {
  try {
    const response = await BaseApiClient.makeAuthenticatedRequest(
      `${API_BASE_URL}/picasso/video/admin/pin_video/${videoId}`,
      {
        method: "POST",
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        getErrorMessageByStatus(response.status, "pin video") + 
        (errorData.detail ? `: ${errorData.detail}` : "")
      )
    }

    const data = await response.json()
    return data
  } catch (err) {
    throw new Error(BaseApiClient.handleApiError(err, "pin video"))
  }
}

/**
 * Unpin a video (works for both admin and user contexts)
 * @param videoId - The video ID to unpin
 * @returns Promise<PinVideoResponse>
 */
export const unpinVideo = async (videoId: string): Promise<PinVideoResponse> => {
  try {
    const response = await BaseApiClient.makeAuthenticatedRequest(
      `${API_BASE_URL}/picasso/video/admin/unpin_video/${videoId}`,
      {
        method: "POST",
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        getErrorMessageByStatus(response.status, "unpin video") + 
        (errorData.detail ? `: ${errorData.detail}` : "")
      )
    }

    const data = await response.json()
    return data
  } catch (err) {
    throw new Error(BaseApiClient.handleApiError(err, "unpin video"))
  }
}

/**
 * Shared helper function to fetch videos from an endpoint
 * @param endpoint - API endpoint path
 * @param operationName - Name of the operation for error messages
 * @param limit - Maximum number of videos to return
 * @param offset - Number of videos to skip
 * @returns Promise<VideoListResponse>
 */
const fetchVideoList = async (
  endpoint: string,
  operationName: string,
  limit: number = 100,
  offset: number = 0
): Promise<VideoListResponse> => {
  try {
    const params: QueryParams = { limit, offset }
    const url = BaseApiClient.buildUrlWithParams(`${API_BASE_URL}${endpoint}`, params)
    
    const response = await BaseApiClient.makeAuthenticatedRequest(url, {
      method: "GET",
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        getErrorMessageByStatus(response.status, operationName) + 
        (errorData.detail ? `: ${errorData.detail}` : "")
      )
    }

    const data = await response.json()
    return {
      success: data.success,
      videos: data.videos || [],
      total_count: data.total || 0,
      limit: data.limit,
      offset: data.offset,
      has_more: data.has_more,
    }
  } catch (err) {
    throw new Error(BaseApiClient.handleApiError(err, operationName))
  }
}

/**
 * Shared helper function to toggle video status (favourite/archive)
 * @param endpoint - API endpoint path
 * @param operationName - Name of the operation for error messages
 * @returns Promise<FavouriteResponse | ArchiveResponse>
 */
const toggleVideoStatus = async (
  endpoint: string,
  operationName: string
): Promise<FavouriteResponse | ArchiveResponse> => {
  try {
    const response = await BaseApiClient.makeAuthenticatedRequest(
      `${API_BASE_URL}${endpoint}`,
      { method: "POST" }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        getErrorMessageByStatus(response.status, operationName) + 
        (errorData.detail ? `: ${errorData.detail}` : "")
      )
    }

    return await response.json()
  } catch (err) {
    throw new Error(BaseApiClient.handleApiError(err, operationName))
  }
}

/**
 * Get user's favourite videos
 * @param limit - Maximum number of videos to return
 * @param offset - Number of videos to skip
 * @returns Promise<VideoListResponse>
 */
export const fetchFavouriteVideos = async (
  limit: number = 100,
  offset: number = 0
): Promise<VideoListResponse> => {
  return fetchVideoList("/picasso/video/favourites", "fetch favourite videos", limit, offset)
}

/**
 * Toggle favourite status for a video
 * @param videoId - The video ID to toggle favourite
 * @returns Promise<FavouriteResponse>
 */
export const toggleVideoFavourite = async (videoId: string): Promise<FavouriteResponse> => {
  return toggleVideoStatus(`/picasso/video/favourite/${videoId}`, "toggle favourite") as Promise<FavouriteResponse>
}

/**
 * Toggle archive status for a video (admin only)
 * @param videoId - The video ID to toggle archive
 * @returns Promise<ArchiveResponse>
 */
export const toggleVideoArchive = async (videoId: string): Promise<ArchiveResponse> => {
  return toggleVideoStatus(`/picasso/video/admin/archive/${videoId}`, "toggle archive") as Promise<ArchiveResponse>
}

/**
 * Get archived videos (admin only)
 * @param limit - Maximum number of videos to return
 * @param offset - Number of videos to skip
 * @returns Promise<VideoListResponse>
 */
export const fetchArchivedVideos = async (
  limit: number = 100,
  offset: number = 0
): Promise<VideoListResponse> => {
  return fetchVideoList("/picasso/video/admin/archives", "fetch archived videos", limit, offset)
}

/**
 * Request deletion of own video (content creator only)
 * @param videoId - Video ID to request deletion for
 * @returns Promise<{message: string, video_id: string, title: string, requested_by: string, requested_at: string}>
 */
export const requestVideoDelete = async (videoId: string): Promise<{
  message: string;
  video_id: string;
  delete_request: boolean;
  updated_at: string;
}> => {
  
  return BaseApiClient.patchRequest<{
    message: string;
    video_id: string;
    delete_request: boolean;
    updated_at: string;
  }>(
    `/picasso/video/${videoId}/metadata`,
    { delete_request: true },
    "requesting video deletion"
  )
};

/**
 * Delete a video (works for both admin and user contexts)
 * @param videoId - The video ID to delete
 * @param userEmail - The email of the user making the request
 * @returns Promise<void>
 */
export const deleteVideoAPI = async (
  videoId: string,
  userEmail: string | null,
) => {
  if (!userEmail) {
    throw new Error("No authenticated user found")
  }

  const response = await BaseApiClient.makeAuthenticatedRequest(
    `${API_BASE_URL}/picasso/video/admin/${videoId}`,
    {
      method: "DELETE",
    },
  )

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.detail || "Failed to delete video")
  }
}

/**
 * Fetch user's videos with pagination
 * @param params - Query parameters for filtering and pagination
 * @returns Promise<VideoListResponse>
 */
export const fetchUserVideos = async (params: VideoListParams = {}): Promise<VideoListResponse> => {
  const queryParams: QueryParams = {
    limit: params.limit || 12,
    offset: params.offset || 0,
  }

  if (params.status && params.status !== "all") {
    queryParams.status = params.status
  }
  if (params.sort_by) {
    queryParams.sort_by = params.sort_by
  }
  if (params.video_type && params.video_type !== "all") {
    queryParams.video_type = params.video_type
  }

  return BaseApiClient.getRequest<VideoListResponse>(
    "/picasso/video/list",
    "fetching user videos",
    queryParams
  )
}

/**
 * Fetch all users' videos with pagination (admin function)
 * @param params - Query parameters for filtering and pagination
 * @returns Promise<VideoListResponse>
 */
export const fetchAllUsersVideos = async (params: VideoListParams = {}): Promise<VideoListResponse> => {
  const queryParams: QueryParams = {
    limit: params.limit || 12,
    offset: params.offset || 0,
  }

  if (params.status && params.status !== "all") {
    queryParams.status = params.status
  }
  if (params.user_email && params.user_email !== "all") {
    queryParams.user_email = params.user_email
  }
  if (params.sort_by) {
    queryParams.sort_by = params.sort_by
  }
  if (params.video_type && params.video_type !== "all") {
    queryParams.video_type = params.video_type
  }

  return BaseApiClient.getRequest<VideoListResponse>(
    "/picasso/video/all",
    "fetching all users' videos",
    queryParams
  )
}

/**
 * Generate a new video
 * @param request - Video generation parameters
 * @returns Promise<VideoGenerationResponse>
 */
export const generateVideo = async (request: VideoGenerationRequest): Promise<VideoGenerationResponse> => {
  return BaseApiClient.postRequest<VideoGenerationResponse>(
    "/picasso/video/generate-complete",
    request,
    "generating video"
  )
}

/**
 * Extend an existing video by generating multiple segments
 * @param request - Video extension parameters (video_id, prompt, target_duration)
 * @returns Promise<VideoExtensionResponse>
 */
export const extendVideo = async (request: VideoExtensionRequest): Promise<VideoExtensionResponse> => {
  return BaseApiClient.postRequest<VideoExtensionResponse>(
    "/picasso/video/extend",
    request,
    "extending video"
  )
}

export const getPendingExtensionRequests = async (): Promise<VideoExtensionRequestRecord[]> => {
  return BaseApiClient.getRequest<VideoExtensionRequestRecord[]>(
    "/picasso/extension-requests/video-extensions/pending",
    "fetching pending extension requests"
  )
}

export const getAllExtensionRequests = async (
  page: number = 1,
  pageSize: number = 9,
  userEmail?: string,
  status?: string,
): Promise<PaginatedExtensionRequestsResponse> => {
  const params: Record<string, string | number> = { page, page_size: pageSize }
  if (userEmail && userEmail !== 'all') params.user_email = userEmail
  if (status && status !== 'all') params.status = status
  return BaseApiClient.getRequest<PaginatedExtensionRequestsResponse>(
    "/picasso/extension-requests/video-extensions/all",
    "fetching all extension requests",
    params
  )
}

export const getMyExtensionRequests = async (
  page: number = 1,
  pageSize: number = 9,
  status?: string,
): Promise<PaginatedExtensionRequestsResponse> => {
  return BaseApiClient.getRequest<PaginatedExtensionRequestsResponse>(
    "/picasso/video/my-extension-requests",
    "fetching my extension requests",
    { page, page_size: pageSize, ...(status && status !== 'all' ? { status } : {}) }
  )
}

export const approveExtensionRequest = async (requestId: string): Promise<{ success: boolean; message: string; task_id?: string }> => {
  return BaseApiClient.postRequest(
    "/picasso/extension-requests/video-extensions/approve",
    { request_id: requestId },
    "approving extension request"
  )
}

export const rejectExtensionRequest = async (requestId: string, feedback?: string): Promise<{ success: boolean; message: string }> => {
  return BaseApiClient.postRequest(
    "/picasso/extension-requests/video-extensions/reject",
    { request_id: requestId, feedback },
    "rejecting extension request"
  )
}


/**
 * Upload a video
 * @param video - Video file to upload
 * @param prompt - Video prompt/description
 * @param model - Model used for generation
 * @param tileId - Optional tile ID for tracking
 * @returns Promise<VideoUploadResponse>
 */
export const uploadVideo = async (
  video: File,
  prompt: string,
  model: string,
  tileId?: string
): Promise<VideoUploadResponse> => {
  try {
    const headers = await BaseApiClient.getAuthHeaders()
    const formData = new FormData()
    formData.append("video", video)
    formData.append("prompt", prompt)
    formData.append("model", model)
    if (tileId) {
      formData.append("tile_id", tileId)
    }

    // Remove Content-Type for FormData - browser will set it with boundary
    const uploadHeaders = { ...headers }
    delete uploadHeaders["Content-Type"]

    const response = await fetch(`${API_BASE_URL}/picasso/video/upload`, {
      method: "POST",
      headers: uploadHeaders,
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
    }

    const data: VideoUploadResponse = await response.json()
    return data
  } catch (error) {
    console.error("❌ Error uploading video:", error)
    throw error
  }
}

/**
 * Cancel video generation
 * @param tileId - Tile ID for tracking (required)
 * @returns Promise<{success: boolean, message: string, task_id: string, strategy: string}>
 */
export const cancelVideoGeneration = async (
  tileId?: string
): Promise<{
  success: boolean;
  message: string;
  task_id: string;
  strategy?: string;
}> => {
  return BaseApiClient.postRequest<{
    success: boolean;
    message: string;
    task_id: string;
    strategy?: string;
  }>(
    "/picasso/video/cancel",
    { tile_id: tileId },
    "canceling video generation"
  )
}

/**
 * Update video metadata (admin function)
 * @param videoId - The ID of the video to update
 * @param status - New status for the video
 * @param feedback - Optional feedback text
 * @param delete_request - Optional delete request flag
 * @param title - Optional new title
 * @returns Promise<{message: string, video_id: string, status: string, feedback?: string, updated_at: string}>
 */
export const updateVideoMetadata = async (
  videoId: string, 
  status?: string, 
  feedback?: string,
  delete_request?: boolean,
  title?: string
): Promise<{
  message: string;
  video_id: string;
  status?: string;
  feedback?: string;
  delete_request?: boolean;
  title?: string;
  updated_at: string;
}> => {
  const body: any = {}
  
  if (status !== undefined) body.status = status
  if (feedback !== undefined) body.feedback = feedback.trim() || undefined
  if (delete_request !== undefined) body.delete_request = delete_request
  if (title !== undefined) body.title = title.trim()

  return BaseApiClient.patchRequest<{
    message: string;
    video_id: string;
    status?: string;
    feedback?: string;
    delete_request?: boolean;
    title?: string;
    updated_at: string;
  }>(
    `/picasso/video/${videoId}/metadata`,
    body,
    "updating video metadata"
  )
}

/**
 * Delete video (admin function)
 * @param videoId - The ID of the video to delete
 * @returns Promise<DeleteVideoResponse>
 */
export const adminDeleteVideo = async (videoId: string): Promise<DeleteVideoResponse> => {
  return BaseApiClient.deleteRequest<DeleteVideoResponse>(
    `/picasso/video/admin/${videoId}`,
    "deleting video (admin)"
  )
}

/**
 * Fetch video status counts (admin function)
 * @param userEmail - Optional user email filter
 * @returns Promise<VideoStatusCountsResponse>
 */
export const fetchVideoStatusCounts = async (userEmail?: string): Promise<VideoStatusCountsResponse> => {
  const params: QueryParams = {}
  if (userEmail) {
    params.user_email = userEmail
  }

  return BaseApiClient.getRequest<VideoStatusCountsResponse>(
    "/picasso/video/status-counts-with-duration",
    "fetching video status counts",
    params
  )
}

/**
 * Fetch all video users list (admin function)
 * @returns Promise<UsersResponse>
 */
export const fetchVideoUsers = async (): Promise<UsersResponse> => {
  return BaseApiClient.getRequest<UsersResponse>(
    "/picasso/video/users",
    "fetching video users"
  )
}

export const fetchExtensionRequestUsers = async (): Promise<UsersResponse> => {
  return BaseApiClient.getRequest<UsersResponse>(
    "/picasso/extension-requests/video-extensions/users",
    "fetching extension request users"
  )
}

/**
 * Get video last frame (utility function) 
 * @param signedUrl - Signed URL of the video
 * @returns Promise<string> - Base64 encoded image
 */
export const getVideoLastFrame = async (signedUrl: string): Promise<string> => {
  const headers = await BaseApiClient.getAuthHeaders()
  const url = BaseApiClient.buildUrlWithParams(`${API_BASE_URL}/picasso/video/last-frame`, { url: signedUrl })
  
  const response = await fetch(url, {
    method: "GET",
    headers,
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  // The backend returns a FileResponse (binary image), convert it to base64
  const arrayBuffer = await response.arrayBuffer()
  const bytes = new Uint8Array(arrayBuffer)
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCodePoint(byte)
  }
  return btoa(binary)
}

/**
 * Download video through backend (to handle CORS issues with signed URLs)
 * Returns the video file as a blob for direct download
 * @param videoId - The ID of the video to download
 * @returns Promise<Blob> - Video file as blob
 */
export const getVideoForDownload = async (videoId: string): Promise<Blob> => {
  const headers = await BaseApiClient.getAuthHeaders()
  const url = BaseApiClient.buildUrlWithParams(`${API_BASE_URL}/picasso/video/download/${videoId}`, {})
  
  const response = await fetch(url, {
    method: "GET",
    headers,
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  // The backend returns a StreamingResponse (binary video), return as blob
  return await response.blob()
}
